import React from 'react';
import { Body5, Container } from '@ui';

const DAYS_THRESHOLD = 60;

/**
 * Calculate the default quarter end date based on current date and 60-day threshold
 * Returns format like "Sep 2025"
 */
export function getDefaultQuarterEnd() {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 0-indexed

  // Quarter end months: Mar (3), Jun (6), Sep (9), Dec (12)
  const quarterEndMonths = [3, 6, 9, 12];

  // Find the most recent quarter end
  let mostRecentQuarterMonth = 12;
  let mostRecentQuarterYear = currentYear - 1;

  for (let i = quarterEndMonths.length - 1; i >= 0; i--) {
    const qMonth = quarterEndMonths[i];
    if (qMonth <= currentMonth) {
      mostRecentQuarterMonth = qMonth;
      mostRecentQuarterYear = currentYear;
      break;
    }
  }

  // Check if most recent quarter end was within 60 days
  const lastDayOfQuarter = new Date(mostRecentQuarterYear, mostRecentQuarterMonth, 0);
  const daysSinceQuarterEnd = Math.floor((today - lastDayOfQuarter) / (1000 * 60 * 60 * 24));

  if (daysSinceQuarterEnd <= DAYS_THRESHOLD) {
    // Use most recent quarter end
    return formatQuarterDate(mostRecentQuarterMonth, mostRecentQuarterYear);
  } else {
    // Use next upcoming quarter end
    const nextQuarterIndex = quarterEndMonths.findIndex((m) => m > currentMonth);
    if (nextQuarterIndex !== -1) {
      return formatQuarterDate(quarterEndMonths[nextQuarterIndex], currentYear);
    } else {
      return formatQuarterDate(3, currentYear + 1); // Next year Q1
    }
  }
}

/**
 * Calculate the default fiscal year end date based on current date and 60-day threshold
 * Returns format like "Dec 2025"
 */
export function getDefaultFiscalYearEnd() {
  const today = new Date();
  const currentYear = today.getFullYear();

  // Fiscal year end is December 31
  const lastDayOfLastYear = new Date(currentYear - 1, 12, 0);
  const daysSinceYearEnd = Math.floor((today - lastDayOfLastYear) / (1000 * 60 * 60 * 24));

  if (daysSinceYearEnd <= DAYS_THRESHOLD) {
    // Use previous year end
    return `Dec ${currentYear - 1}`;
  } else {
    // Use current year end
    return `Dec ${currentYear}`;
  }
}

/**
 * Format quarter month and year to "Mon YYYY" format
 */
function formatQuarterDate(month, year) {
  const monthNames = { 3: 'Mar', 6: 'Jun', 9: 'Sep', 12: 'Dec' };
  return `${monthNames[month]} ${year}`;
}

/**
 * Calculate year-over-year date (1 year prior)
 */
export function calculateYoYDate(dateString) {
  // Parse "Mon YYYY" format
  const parts = dateString.split(' ');
  if (parts.length !== 2) return dateString;

  const month = parts[0];
  const year = parseInt(parts[1], 10);

  return `${month} ${year - 1}`;
}

/**
 * Generate quarter end options for the last 3 years and next year
 */
function generateQuarterOptions() {
  const today = new Date();
  const currentYear = today.getFullYear();
  const options = [];
  const quarters = ['Mar', 'Jun', 'Sep', 'Dec'];

  // Generate options from 3 years ago to 1 year in the future
  for (let year = currentYear - 3; year <= currentYear + 1; year++) {
    quarters.forEach((quarter) => {
      options.push(`${quarter} ${year}`);
    });
  }

  return options;
}

/**
 * Generate fiscal year end options for the last 3 years and next year
 */
function generateFiscalYearOptions() {
  const today = new Date();
  const currentYear = today.getFullYear();
  const options = [];

  // Generate options from 3 years ago to 1 year in the future
  for (let year = currentYear - 3; year <= currentYear + 1; year++) {
    options.push(`Dec ${year}`);
  }

  return options;
}

/**
 * DateInput component for selecting quarter end or fiscal year end dates
 */
export function DateInput({ value, label, onChange, isQuarterEnd }) {
  const options = isQuarterEnd ? generateQuarterOptions() : generateFiscalYearOptions();

  return (
    <Container>
      <Container>
        <Body5>{label}</Body5>
      </Container>
      <Container top={8}>
        <select value={value} onChange={onChange}>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </Container>
    </Container>
  );
}

export function QuarterDateInput({ label, value, onChange, isQuarterEnd }) {
  return <DateInput label={label} value={value} onChange={onChange} isQuarterEnd={isQuarterEnd} />;
}
